import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { getSelectionOverrides } from 'modules/shared-menu/api';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { OfferOverridesEntry, OfferingPlatformScope, SelectionOverridesResponse } from 'modules/shared-menu/types';

import { useSelectionInfoContext } from '../../../contexts/SelectionInfoContext';

const buildQueryKey = (venueId: string) => ['venue-selection-overrides', venueId];

type UseLoadVenueSelectionOverridesProps = {
  enabled: boolean;
  selectionId: string;
  scope: OfferingPlatformScope;
};

export const useLoadVenueSelectionOverrides = ({
  enabled,
  selectionId,
  scope,
}: UseLoadVenueSelectionOverridesProps) => {
  const { data, isInitialLoading } = useQuery(
    buildQueryKey(scope.id),
    () => getSelectionOverrides(selectionId, scope),
    {
      staleTime: Infinity,
      retry: false,
      enabled,
    },
  );

  return { selectionOverrides: data, loadingSelectionOverrides: isInitialLoading };
};

export const useVenueSelectionOverrides = () => {
  const queryClient = useQueryClient();
  const scope = useScopeContext();
  const queryKey = useMemo(() => buildQueryKey(scope.id), [scope.id]);
  const { vlmeConfig, selection } = useSelectionInfoContext();
  const { data, refetch } = useQuery(queryKey, () => getSelectionOverrides(selection.id, scope), {
    staleTime: Infinity,
    retry: false,
    enabled: !!vlmeConfig && vlmeConfig.isSharedVenue,
  });

  if (!data && vlmeConfig && vlmeConfig.isSingleVenue === false) {
    throw new Error('No selection overrides data found, this should not happen!');
  }

  const updateSelectionOverridesCache = useCallback(
    (newData: OfferOverridesEntry[]) => {
      queryClient.setQueryData(queryKey, (oldData: SelectionOverridesResponse | undefined) => {
        const newOverrides = newData.reduce<SelectionOverridesResponse['offers']>(
          (acc, curr) => ({
            ...acc,
            [curr.offer_id]: curr.offer_overrides,
          }),
          {},
        );

        return {
          offers: {
            ...oldData?.offers,
            ...newOverrides,
          },
        };
      });
    },
    [queryClient, queryKey],
  );

  return { selectionOverrides: data, refetchSelectionOverrides: refetch, updateSelectionOverridesCache };
};
